import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rfqApi } from '../../services/apiService';
import './MobileRequestQuotationHubPage.css';

const STATUS_OPTIONS = ['All', 'new', 'replied', 'closed', 'in_progress'];

const Balloon = () => (
  <svg className="mrq-balloon" viewBox="0 0 120 100" fill="none" aria-hidden>
    <ellipse cx="60" cy="88" rx="28" ry="5" fill="currentColor" opacity="0.2" />
    <path d="M60 18 C35 18 25 38 25 52 C25 68 40 78 60 78 C80 78 95 68 95 52 C95 38 85 18 60 18Z" fill="currentColor" opacity="0.25" stroke="currentColor" strokeWidth="1.5" />
    <rect x="48" y="76" width="24" height="14" rx="2" fill="currentColor" opacity="0.4" />
  </svg>
);

const labelForStatus = (s) => {
  if (s === 'All') return 'All';
  if (s === 'in_progress') return 'In progress';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const MobileRequestQuotationHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('All');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (status !== 'All') params.status = status;
      const data = await rfqApi.getByUserId(user.id, params);
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, status]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => {
      const blob = [r.details, r.category, r.companyName, r.fullName, r.email].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [list, search]);

  return (
    <div className="mrq-page">
      <header className="mrq-header">
        <div className="mrq-header-left">
          <button type="button" className="mrq-back" onClick={() => navigate(-1)} aria-label="Back">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="mrq-title">Request for Quotation</h1>
        </div>
        <button type="button" className="mrq-post" onClick={() => navigate('/submit-rfq')}>
          Post
        </button>
      </header>

      <div className="mrq-toolbar">
        {searchOpen && (
          <input
            className="mrq-search-input"
            placeholder="Search RFQs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search RFQs"
            autoFocus
          />
        )}
        <div className="mrq-filter-wrap">
          <button type="button" className="mrq-filter" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen}>
            {labelForStatus(status)}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {menuOpen && (
            <div className="mrq-dropdown" role="listbox">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={s === status ? 'is-active' : ''}
                  onClick={() => {
                    setStatus(s);
                    setMenuOpen(false);
                  }}
                >
                  {labelForStatus(s)}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="mrq-search-btn"
          aria-label={searchOpen ? 'Close search' : 'Search'}
          onClick={() => {
            setSearchOpen((o) => !o);
            if (searchOpen) setSearch('');
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {searchOpen ? (
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            ) : (
              <g>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </g>
            )}
          </svg>
        </button>
      </div>

      <div className="mrq-body">
        {loading ? (
          <div className="mrq-loading">Loading…</div>
        ) : error ? (
          <div className="mrq-loading">
            <p>{error}</p>
            <button type="button" className="mrq-post" onClick={load}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mrq-empty">
            <Balloon />
            <p>No Items</p>
          </div>
        ) : (
          filtered.map((r) => (
            <button type="button" key={r.id} className="mrq-card" onClick={() => navigate('/rfq')}>
              <p className="mrq-card-title">{r.category || 'RFQ'}</p>
              <p className="mrq-card-sub">{(r.details || '').slice(0, 80)}{(r.details || '').length > 80 ? '…' : ''}</p>
              <span className="mrq-status">{r.status?.replace('_', ' ') || 'new'}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default MobileRequestQuotationHubPage;
